/**
 * Opmerking per account direct in het overzicht (Joris 28 sep). Zelfde gedrag als de target-cel:
 * typen, Enter of wegklikken bewaart (Enter springt naar de volgende regel), Esc zet terug.
 * Het is dezelfde notitie als in het detailpaneel; versiecontrole voorkomt stil overschrijven.
 */
import React, { useEffect, useRef, useState } from 'react';
import { InputBase, Tooltip } from '@mui/material';
import toast from 'react-hot-toast';
import { biedOngedaanAan } from '../../utils/ongedaan';
import { budgetApi } from './budgetApi';

const SELECTOR = 'textarea[data-opmerking-cel], input[data-opmerking-cel]';
const MAX = 5000;

function focusBuur(el, stap) {
  const alle = [...document.querySelectorAll(SELECTOR)];
  const volgende = alle[alle.indexOf(el) + stap];
  if (volgende) volgende.focus();
}

export default function OpmerkingCel({ regel, jaar, magWijzigen, onOpgeslagen }) {
  const [waarde, setWaarde] = useState(regel.notitie || '');
  const [bezig, setBezig] = useState(false);
  const gefocust = useRef(false);
  const annuleer = useRef(false);
  const gewijzigd = useRef(false);
  const start = useRef({ notitie: regel.notitie || '', versie: regel.versie });

  useEffect(() => { if (!gefocust.current && !gewijzigd.current) setWaarde(regel.notitie || ''); }, [regel.notitie]);

  if (!magWijzigen) {
    return regel.notitie
      ? <Tooltip title={regel.notitie}><span style={{ color: '#475569' }}>{regel.notitie.length > 60 ? `${regel.notitie.slice(0, 60)}…` : regel.notitie}</span></Tooltip>
      : null;
  }

  const bewaar = async () => {
    if (!gewijzigd.current) { setWaarde(regel.notitie || ''); return; }
    const nieuw = waarde.trim();
    const { notitie: oud, versie } = start.current;
    if (nieuw === (oud || '').trim()) { gewijzigd.current = false; setWaarde(regel.notitie || ''); return; }
    setBezig(true);
    try {
      const uit = await budgetApi.bewaar(regel.sleutel, { notitie: nieuw || null, versie }, jaar);
      gewijzigd.current = false;
      await onOpgeslagen();
      biedOngedaanAan(`Opmerking ${regel.naam} opgeslagen`, async () => {
        await budgetApi.bewaar(regel.sleutel, { ...uit.herstel, versie: uit.versie }, jaar);
        await onOpgeslagen();
      }, { eigenKnop: true });
    } catch (e) {
      if (e.status === 409) {
        toast.error(`${regel.naam} is intussen door iemand anders gewijzigd — je opmerking staat er nog; druk opnieuw op Enter om te bewaren`);
        await onOpgeslagen();
        start.current = { notitie: regel.notitie || '', versie: e.huidig?.versie ?? regel.versie };
      } else {
        toast.error(e.message);
      }
    } finally { setBezig(false); }
  };

  return (
    <InputBase
      multiline
      maxRows={3}
      value={waarde}
      disabled={bezig}
      placeholder="Opmerking…"
      onChange={(e) => { gewijzigd.current = true; setWaarde(e.target.value.slice(0, MAX)); }}
      onClick={(e) => e.stopPropagation()}
      onFocus={() => {
        gefocust.current = true;
        if (!gewijzigd.current) start.current = { notitie: regel.notitie || '', versie: regel.versie };
      }}
      onBlur={() => { gefocust.current = false; if (annuleer.current) { annuleer.current = false; return; } bewaar(); }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); const el = e.target; el.blur(); focusBuur(el, 1); }
        if (e.key === 'Escape') { annuleer.current = true; gewijzigd.current = false; setWaarde(regel.notitie || ''); e.target.blur(); }
      }}
      inputProps={{ 'data-opmerking-cel': regel.sleutel, 'aria-label': `Opmerking ${regel.naam}`, maxLength: MAX }}
      sx={{
        width: 240, fontSize: 13, color: '#334155',
        '& textarea, & input': {
          px: 1, py: 0.5, borderRadius: 0.75, border: '1px solid transparent', bgcolor: '#fafafa',
          '&:hover': { borderColor: '#e2e8f0' },
          '&:focus': { borderColor: '#4f46e5', bgcolor: '#fff', boxShadow: '0 0 0 2px #e0e7ff' },
        },
      }}
    />
  );
}
